import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

export type Language = "pt" | "en";

// Translations type
type TranslationKeys = {
  // Navigation
  nav: {
    home: string;
    stock: string;
    services: string;
    about: string;
    contact: string;
    admin: string;
    login: string;
    logout: string;
  };
  // Hero
  hero: {
    title: string;
    subtitle: string;
    cta: string;
    search: string;
    statsVehicles: string;
    statsYears: string;
    statsClients: string;
  };
  // Recently Sold
  recentlySold: {
    title: string;
    subtitle: string;
    sold: string;
  };
  // Services
  services: {
    title: string;
    subtitle: string;
    financing: {
      title: string;
      description: string;
    };
    warranty: {
      title: string;
      description: string;
    };
    tradeIn: {
      title: string;
      description: string;
    };
    inspection: {
      title: string;
      description: string;
    };
    documentation: {
      title: string;
      description: string;
    };
    delivery: {
      title: string;
      description: string;
    };
  };
  // About
  about: {
    title: string;
    subtitle: string;
    description: string;
    experience: string;
    experienceText: string;
    quality: string;
    qualityText: string;
    trust: string;
    trustText: string;
  };
  // Contact
  contact: {
    title: string;
    subtitle: string;
    whatsapp: string;
    call: string;
    location: string;
    hours: string;
    hoursText: string;
  };
  // Stock/Filters
  stock: {
    title: string;
    subtitle: string;
    filters: string;
    clearFilters: string;
    showFilters: string;
    hideFilters: string;
    noResults: string;
    sortBy: string;
    priceAsc: string;
    priceDesc: string;
    yearDesc: string;
    kmAsc: string;
    brand: string;
    model: string;
    year: string;
    fuel: string;
    transmission: string;
    segment: string;
    color: string;
    priceRange: string;
    kmRange: string;
    displacement: string;
    power: string;
    from: string;
    to: string;
    manual: string;
    automatic: string;
  };
  // Car details
  car: {
    price: string;
    year: string;
    km: string;
    fuel: string;
    transmission: string;
    color: string;
    displacement: string;
    power: string;
    segment: string;
    description: string;
    specifications: string;
    gallery: string;
    interestedIn: string;
    callUs: string;
    messageWhatsapp: string;
  };
  // Fuel types
  fuel: {
    gasoline: string;
    diesel: string;
    hybrid: string;
    electric: string;
    lpg: string;
  };
  // Segments
  segments: {
    suv: string;
    sedan: string;
    hatchback: string;
    wagon: string;
    coupe: string;
    convertible: string;
    van: string;
    pickup: string;
  };
  // Footer
  footer: {
    rights: string;
    privacy: string;
    terms: string;
  };
  // Admin
  admin: {
    dashboard: string;
    totalCars: string;
    available: string;
    sold: string;
    archived: string;
    addCar: string;
    editCar: string;
    manageCars: string;
    archive: string;
    delete: string;
    confirmArchive: string;
    confirmDelete: string;
    uploadImages: string;
    dragDrop: string;
    save: string;
    cancel: string;
    status: string;
    actions: string;
  };
  // Common
  common: {
    loading: string;
    error: string;
    success: string;
    viewAll: string;
    viewDetails: string;
    back: string;
    next: string;
    previous: string;
  };
};

const translations: Record<Language, TranslationKeys> = {
  pt: {
    nav: {
      home: "Início",
      stock: "Stock",
      services: "Serviços",
      about: "Sobre Nós",
      contact: "Contactos",
      admin: "Administração",
      login: "Entrar",
      logout: "Sair",
    },
    hero: {
      title: "O Seu Próximo Carro Está Aqui",
      subtitle: "Qualidade, confiança e os melhores preços do mercado.\nEncontre o veículo perfeito para si.",
      cta: "Explorar Stock",
      search: "Pesquisar veículos...",
      statsVehicles: "Veículos Vendidos",
      statsYears: "Anos",
      statsClients: "Clientes",
    },
    recentlySold: {
      title: "Vendidos Recentemente",
      subtitle: "Veja os nossos últimos negócios de sucesso",
      sold: "VENDIDO",
    },
    services: {
      title: "Os Nossos Serviços",
      subtitle: "Tudo o que precisa num só lugar",
      financing: {
        title: "Financiamento",
        description: "Soluções de financiamento personalizadas com as melhores taxas do mercado.",
      },
      warranty: {
        title: "Garantia",
        description: "Todos os nossos veículos incluem garantia para sua total tranquilidade.",
      },
      tradeIn: {
        title: "Retoma",
        description: "Avaliamos o seu carro atual e oferecemos o melhor valor de retoma.",
      },
      inspection: {
        title: "Inspeção",
        description: "Veículos inspecionados e certificados antes da venda.",
      },
      documentation: {
        title: "Documentação",
        description: "Tratamos de toda a documentação e transferência de propriedade.",
      },
      delivery: {
        title: "Entrega",
        description: "Entregamos o seu veículo em qualquer ponto do país.",
      },
    },
    about: {
      title: "Sobre a PassCar",
      subtitle: "A sua confiança é o nosso combustível",
      description:
        "Na Passcar, temos como objetivo ir além da venda de carros, proporcionando aos nossos clientes confiança, qualidade e um atendimento diferenciado. Cada veículo em nosso portfólio é cuidadosamente selecionado para garantir que você faça a escolha certa, seja para uso pessoal ou profissional. Um dos nossos grandes diferenciais é termos a nossa oficina própria a Setauto, que conta com mais de 11 anos de expertise no mercado. Isso nos permite oferecer um serviço completo e suporte técnico eficiente, especialmente em casos de garantia. Com essa estrutura de apoio, nossos clientes têm a tranquilidade de saber que, ao adquirir um veículo na Passcar, contam com uma equipe pronta para garantir a manutenção e o bom funcionamento do carro por muitos anos. A nossa missão é oferecer uma experiência de compra segura, transparente e personalizada, sempre colocando a satisfação e o bem-estar de nossos clientes em primeiro lugar.",
      experience: "Experiência",
      experienceText: "Anos de experiência no mercado automóvel",
      quality: "Clientes",
      qualityText: "Clientes satisfeitos com o nosso serviço",
      trust: "Confiança",
      trustText: "Compromisso total com a satisfação do cliente",
    },
    contact: {
      title: "Contacte-nos",
      subtitle: "Estamos aqui para ajudar",
      whatsapp: "Enviar Mensagem",
      call: "Ligar Agora",
      location: "Localização",
      hours: "Horário",
      hoursText: "Segunda a Sábado: 9h - 19h",
    },
    stock: {
      title: "O Nosso Stock",
      subtitle: "Encontre o carro perfeito para si",
      filters: "Filtros",
      clearFilters: "Limpar Filtros",
      showFilters: "Mostrar Filtros",
      hideFilters: "Esconder Filtros",
      noResults: "Nenhum veículo encontrado com os filtros selecionados.",
      sortBy: "Ordenar por",
      priceAsc: "Preço (menor)",
      priceDesc: "Preço (maior)",
      yearDesc: "Ano (mais recente)",
      kmAsc: "Quilómetros (menor)",
      brand: "Marca",
      model: "Modelo",
      year: "Ano",
      fuel: "Combustível",
      transmission: "Transmissão",
      segment: "Segmento",
      color: "Cor",
      priceRange: "Preço",
      kmRange: "Quilómetros",
      displacement: "Cilindrada",
      power: "Potência",
      from: "De",
      to: "Até",
      manual: "Manual",
      automatic: "Automático",
    },
    car: {
      price: "Preço",
      year: "Ano",
      km: "Quilómetros",
      fuel: "Combustível",
      transmission: "Transmissão",
      color: "Cor",
      displacement: "Cilindrada",
      power: "Potência",
      segment: "Segmento",
      description: "Descrição",
      specifications: "Especificações",
      gallery: "Galeria",
      interestedIn: "Tenho interesse neste veículo",
      callUs: "Ligar",
      messageWhatsapp: "Olá! Estou interessado no",
    },
    fuel: {
      gasoline: "Gasolina",
      diesel: "Gasóleo",
      hybrid: "Híbrido",
      electric: "Elétrico",
      lpg: "GPL",
    },
    segments: {
      suv: "SUV",
      sedan: "Sedan",
      hatchback: "Hatchback",
      wagon: "Carrinha",
      coupe: "Coupé",
      convertible: "Descapotável",
      van: "Monovolume",
      pickup: "Pick-up",
    },
    footer: {
      rights: "Todos os direitos reservados.",
      privacy: "Política de Privacidade",
      terms: "Termos e Condições",
    },
    admin: {
      dashboard: "Painel de Controlo",
      totalCars: "Total de Carros",
      available: "Disponíveis",
      sold: "Vendidos",
      archived: "Arquivados",
      addCar: "Adicionar Carro",
      editCar: "Editar Carro",
      manageCars: "Gerir Carros",
      archive: "Arquivar",
      delete: "Eliminar",
      confirmArchive: "Tem a certeza que deseja arquivar este veículo?",
      confirmDelete: "Tem a certeza que deseja eliminar permanentemente este veículo?",
      uploadImages: "Carregar Imagens",
      dragDrop: "Arraste as imagens ou clique para selecionar",
      save: "Guardar",
      cancel: "Cancelar",
      status: "Estado",
      actions: "Ações",
    },
    common: {
      loading: "A carregar...",
      error: "Ocorreu um erro",
      success: "Sucesso!",
      viewAll: "Ver Todos",
      viewDetails: "Ver Detalhes",
      back: "Voltar",
      next: "Seguinte",
      previous: "Anterior",
    },
  },
  en: {
    nav: {
      home: "Home",
      stock: "Stock",
      services: "Services",
      about: "About Us",
      contact: "Contacts",
      admin: "Admin",
      login: "Login",
      logout: "Logout",
    },
    hero: {
      title: "Your Next Car Is Here",
      subtitle: "Quality, trust and the best prices on the market.\nFind the perfect vehicle for you.",
      cta: "Explore Stock",
      search: "Search vehicles...",
      statsVehicles: "Vehicles Sold",
      statsYears: "Years",
      statsClients: "Clients",
    },
    recentlySold: {
      title: "Recently Sold",
      subtitle: "See our latest successful deals",
      sold: "SOLD",
    },
    services: {
      title: "Our Services",
      subtitle: "Everything you need in one place",
      financing: {
        title: "Financing",
        description: "Personalized financing solutions with the best market rates.",
      },
      warranty: {
        title: "Warranty",
        description: "All our vehicles include warranty for your peace of mind.",
      },
      tradeIn: {
        title: "Trade-In",
        description: "We evaluate your current car and offer the best trade-in value.",
      },
      inspection: {
        title: "Inspection",
        description: "Vehicles inspected and certified before sale.",
      },
      documentation: {
        title: "Documentation",
        description: "We handle all documentation and ownership transfer.",
      },
      delivery: {
        title: "Delivery",
        description: "We deliver your vehicle anywhere in the country.",
      },
    },
    about: {
      title: "About PassCar",
      subtitle: "Your trust is our fuel",
      description: "At Passcar, our goal goes beyond just selling cars; we strive to provide our clients with trust, quality, and a personalized service experience. Each vehicle in our portfolio is carefully selected to ensure you make the right choice, whether for personal or professional use. One of our main advantages is having our own workshop, Setauto, with over 11 years of market expertise. This allows us to offer complete service and efficient technical support, especially regarding warranties. With this support structure, our customers have the peace of mind of knowing that, when purchasing a vehicle at Passcar, they have a team ready to ensure the maintenance and proper functioning of the car for many years. Our mission is to provide a safe, transparent, and personalized buying experience, always putting the satisfaction and well-being of our customers first.",
      experience: "Experience",
      experienceText: "Years of experience in the automotive market",
      quality: "Clients",
      qualityText: "Satisfied customers with our service",
      trust: "Trust",
      trustText: "Total commitment to customer satisfaction",
    },
    contact: {
      title: "Contact Us",
      subtitle: "We are here to help",
      whatsapp: "Send Message",
      call: "Call Now",
      location: "Location",
      hours: "Hours",
      hoursText: "Monday to Saturday: 9am - 7pm",
    },
    stock: {
      title: "Our Stock",
      subtitle: "Find the perfect car for you",
      filters: "Filters",
      clearFilters: "Clear Filters",
      showFilters: "Show Filters",
      hideFilters: "Hide Filters",
      noResults: "No vehicles found with selected filters.",
      sortBy: "Sort by",
      priceAsc: "Price (lowest)",
      priceDesc: "Price (highest)",
      yearDesc: "Year (newest)",
      kmAsc: "Kilometers (lowest)",
      brand: "Brand",
      model: "Model",
      year: "Year",
      fuel: "Fuel",
      transmission: "Transmission",
      segment: "Segment",
      color: "Color",
      priceRange: "Price",
      kmRange: "Kilometers",
      displacement: "Displacement",
      power: "Power",
      from: "From",
      to: "To",
      manual: "Manual",
      automatic: "Automatic",
    },
    car: {
      price: "Price",
      year: "Year",
      km: "Kilometers",
      fuel: "Fuel",
      transmission: "Transmission",
      color: "Color",
      displacement: "Displacement",
      power: "Power",
      segment: "Segment",
      description: "Description",
      specifications: "Specifications",
      gallery: "Gallery",
      interestedIn: "I'm interested in this vehicle",
      callUs: "Call",
      messageWhatsapp: "Hello! I'm interested in the",
    },
    fuel: {
      gasoline: "Gasoline",
      diesel: "Diesel",
      hybrid: "Hybrid",
      electric: "Electric",
      lpg: "LPG",
    },
    segments: {
      suv: "SUV",
      sedan: "Sedan",
      hatchback: "Hatchback",
      wagon: "Wagon",
      coupe: "Coupe",
      convertible: "Convertible",
      van: "Van",
      pickup: "Pickup",
    },
    footer: {
      rights: "All rights reserved.",
      privacy: "Privacy Policy",
      terms: "Terms and Conditions",
    },
    admin: {
      dashboard: "Dashboard",
      totalCars: "Total Cars",
      available: "Available",
      sold: "Sold",
      archived: "Archived",
      addCar: "Add Car",
      editCar: "Edit Car",
      manageCars: "Manage Cars",
      archive: "Archive",
      delete: "Delete",
      confirmArchive: "Are you sure you want to archive this vehicle?",
      confirmDelete: "Are you sure you want to permanently delete this vehicle?",
      uploadImages: "Upload Images",
      dragDrop: "Drag images or click to select",
      save: "Save",
      cancel: "Cancel",
      status: "Status",
      actions: "Actions",
    },
    common: {
      loading: "Loading...",
      error: "An error occurred",
      success: "Success!",
      viewAll: "View All",
      viewDetails: "View Details",
      back: "Back",
      next: "Next",
      previous: "Previous",
    },
  },
};

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("passcar-language");
      if (saved === "pt" || saved === "en") return saved;
    }
    return "pt";
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("passcar-language", lang);
  }, []);

  const value: I18nContextType = {
    language,
    setLanguage,
    t: translations[language],
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}
